import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../../hooks/useLanguage';
import LangToggle from './LangToggle';

type ActiveStrip = null | 'apps' | 'models';

const Nav: React.FC = () => {
  const [language] = useLanguage();
  const en = language === 'en';
  const location = useLocation();
  const [activeStrip, setActiveStrip] = React.useState<ActiveStrip>(null);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const modelsTriggerRef = React.useRef<HTMLAnchorElement>(null);
  const appsTriggerRef = React.useRef<HTMLAnchorElement>(null);
  const modelsStripRef = React.useRef<HTMLDivElement>(null);
  const appsStripRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setMenuOpen(false);
    setActiveStrip(null);
  }, [location.pathname]);

  // Position each dropdown horizontally centered under its trigger. We
  // measure once on mount, then again whenever the language, viewport width,
  // or active strip changes (all can shift trigger positions or reveal the
  // strip for the first time). CSS reads the offset via `--strip-cx`.
  React.useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const align = () => {
      const wrapRect = wrap.getBoundingClientRect();
      const set = (
        trigger: HTMLAnchorElement | null,
        strip: HTMLDivElement | null,
      ) => {
        if (!trigger || !strip) return;
        const r = trigger.getBoundingClientRect();
        const cx = r.left + r.width / 2 - wrapRect.left;
        strip.style.setProperty('--strip-cx', `${cx}px`);
      };
      set(modelsTriggerRef.current, modelsStripRef.current);
      set(appsTriggerRef.current, appsStripRef.current);
    };
    align();
    window.addEventListener('resize', align);
    return () => window.removeEventListener('resize', align);
  }, [language, activeStrip]);

  React.useEffect(() => {
    if (!activeStrip) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setActiveStrip(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveStrip(null);
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [activeStrip]);

  const openStrip = (id: Exclude<ActiveStrip, null>) => setActiveStrip(id);
  const closeStrip = (id: Exclude<ActiveStrip, null>) =>
    setActiveStrip((prev) => (prev === id ? null : prev));
  const toggleStrip = (id: Exclude<ActiveStrip, null>) =>
    setActiveStrip((prev) => (prev === id ? null : id));

  const wrapClass = [
    'nav__wrap',
    activeStrip ? 'is-strip-open' : '',
    menuOpen ? 'is-menu-open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const modelsOpen = activeStrip === 'models';
  const appsOpen = activeStrip === 'apps';

  return (
    <div className={wrapClass} ref={wrapRef}>
      <nav className="nav">
        <Link className="nav__logo" to="/" onClick={() => setMenuOpen(false)}>
          Thoughtful Oasis
        </Link>
        <div className="nav__panel">
          <div className="nav__links">
            <Link to="/research">{en ? 'Research' : 'Onderzoek'}</Link>
            <div
              className={`nav__item nav__item--has-menu nav__item--menu-models${modelsOpen ? ' is-open' : ''}`}
              onMouseEnter={menuOpen ? undefined : () => openStrip('models')}
              onMouseLeave={menuOpen ? undefined : () => closeStrip('models')}
            >
              <Link
                to="/models"
                ref={modelsTriggerRef}
                aria-haspopup="true"
                aria-expanded={modelsOpen}
                onClick={(e) => {
                  if (menuOpen) {
                    e.preventDefault();
                    toggleStrip('models');
                    return;
                  }
                  if (window.matchMedia('(hover: hover)').matches) return;
                  if (modelsOpen) return;
                  e.preventDefault();
                  openStrip('models');
                }}
              >
                Models
              </Link>
              <div className="nav__sub" aria-hidden={!modelsOpen}>
                <Link to="/mission-os">oasis_0 · Prometheus</Link>
                <Link to="/vmo">oasis_0 · Themis</Link>
                <Link to="/models/asclepius-510k">oasis_0 · Asclepius 510k</Link>
                <Link to="/models">
                  {en ? 'All models' : 'Alle modellen'}
                </Link>
              </div>
            </div>
            <div
              className={`nav__item nav__item--has-menu nav__item--menu-apps${appsOpen ? ' is-open' : ''}`}
              onMouseEnter={menuOpen ? undefined : () => openStrip('apps')}
              onMouseLeave={menuOpen ? undefined : () => closeStrip('apps')}
            >
              <Link
                to="/applications"
                ref={appsTriggerRef}
                aria-haspopup="true"
                aria-expanded={appsOpen}
                onClick={(e) => {
                  if (menuOpen) {
                    e.preventDefault();
                    toggleStrip('apps');
                    return;
                  }
                  if (window.matchMedia('(hover: hover)').matches) return;
                  if (appsOpen) return;
                  e.preventDefault();
                  openStrip('apps');
                }}
              >
                {en ? 'Applications' : 'Toepassingen'}
              </Link>
              <div className="nav__sub" aria-hidden={!appsOpen}>
                <Link to="/mission-os">MissionOS</Link>
                <Link to="/vmo">VraagMijnOverheid</Link>
                <Link to="/platform">API</Link>
                <Link to="/applications">
                  {en ? 'All applications' : 'Alle toepassingen'}
                </Link>
              </div>
            </div>
            <Link to="/about">{en ? 'About' : 'Over ons'}</Link>
          </div>
          <div className="nav__actions">
            <LangToggle />
            <Link className="nav__cta" to="/contact">
              {en ? 'Get in touch' : 'Neem contact op'}
            </Link>
          </div>
        </div>
        <button
          type="button"
          className="nav__toggle"
          aria-label="Menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
        >
          <span className="nav__toggle__bars" aria-hidden="true"></span>
        </button>
      </nav>
      <div
        className={`nav__strip nav__strip--models${modelsOpen ? ' is-open' : ''}`}
        ref={modelsStripRef}
        aria-label="Models"
        aria-hidden={!modelsOpen}
        onMouseEnter={() => openStrip('models')}
        onMouseLeave={() => closeStrip('models')}
      >
        <div className="nav__strip__inner">
          <div className="nav__strip__items">
            <Link className="nav__strip__item" to="/mission-os">
              oasis_0 · Prometheus
            </Link>
            <Link className="nav__strip__item" to="/vmo">
              oasis_0 · Themis
            </Link>
            <Link className="nav__strip__item" to="/models/asclepius-510k">
              oasis_0 · Asclepius 510k
            </Link>
          </div>
          <Link className="btn btn-secondary nav__strip__btn" to="/models">
            {en ? 'All models' : 'Alle modellen'}
          </Link>
        </div>
      </div>
      <div
        className={`nav__strip nav__strip--apps${appsOpen ? ' is-open' : ''}`}
        ref={appsStripRef}
        aria-label="Applications"
        aria-hidden={!appsOpen}
        onMouseEnter={() => openStrip('apps')}
        onMouseLeave={() => closeStrip('apps')}
      >
        <div className="nav__strip__inner">
          <div className="nav__strip__items">
            <Link className="nav__strip__item" to="/mission-os">
              MissionOS
            </Link>
            <Link className="nav__strip__item" to="/vmo">
              VraagMijnOverheid
            </Link>
            <Link className="nav__strip__item" to="/platform">
              API
            </Link>
          </div>
          <Link className="btn btn-secondary nav__strip__btn" to="/applications">
            {en ? 'All applications' : 'Alle toepassingen'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Nav;
